/**
 * §19.4 — Türkçe biçimlendirme kuralları.
 *  · Sayı: 1.234,56 (binlik nokta, ondalık virgül)
 *  · Para: ¥12.140 (JPY ondalıksız) · £12,99 (UK paneli) · $53,12
 *  · Yüzde: %20,04 (işaret ÖNDE — Türkçe kuralı, Intl bunu yapmaz)
 *  · Tarih: 23 Ağu 2026 14:32
 */

const TR = 'tr-TR';

const numberFmt = new Intl.NumberFormat(TR, { maximumFractionDigits: 2 });
const intFmt = new Intl.NumberFormat(TR, { maximumFractionDigits: 0 });
const usdFmt = new Intl.NumberFormat(TR, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 1234.5 → "1.234,5" */
export function formatNumber(value: number, fractionDigits?: number): string {
  if (fractionDigits === undefined) return numberFmt.format(value);
  return new Intl.NumberFormat(TR, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/**
 * Pazarın para birimi (13 Ekim 2026, UK paneli). Her süreç tek pazara satıyor:
 * API açılışta `PAZAR`'dan, web açılışta `/system/pazar`'dan ayarlıyor. Ayarlanmazsa
 * JP — bugünkü çıktı birebir aynı.
 */
let paraAyari: { isaret: string; ondalik: number } = { isaret: '¥', ondalik: 0 };

export function paraBiriminiAyarla(ayar: { paraIsareti: string; ondalik: number }): void {
  paraAyari = { isaret: ayar.paraIsareti, ondalik: ayar.ondalik };
}

/** Etkin pazarın para işareti: JP "¥", UK "£", MX "MX$", SA "SAR". */
export function paraIsareti(): string {
  return paraAyari.isaret;
}

/**
 * Tutarın önüne yazılacak işaret: harfle biten işarette arada boşluk ("SAR 12,99",
 * "zł 12,99"); simgede bitişik ("¥12.140", "£12,99", "MX$12,99", "€12,99"). 15 Ekim 2026,
 * SA paneli; 18 Ekim'de Latin dışı harf ("zł") için genişletildi.
 */
export function paraOnEki(): string {
  return /\p{L}$/u.test(paraAyari.isaret) ? `${paraAyari.isaret} ` : paraAyari.isaret;
}

/** Etkin pazarın en küçük birim basamağı: JP 0, UK 2. */
export function paraOndaligi(): number {
  return paraAyari.ondalik;
}

/**
 * Pazar parası — değer EN KÜÇÜK birimde (veritabanındaki `*_jpy` kolonları).
 * JP (§19.4, ondalıksız): 12140 → "¥12.140" · UK: 1299 → "£12,99".
 * Adı tarihsel; anlamı "pazarın parası".
 */
export function formatJpy(value: number): string {
  if (paraAyari.ondalik === 0) return `${paraOnEki()}${intFmt.format(Math.round(value))}`;
  const d = paraAyari.ondalik;
  return `${paraOnEki()}${new Intl.NumberFormat(TR, {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(Math.round(value) / 10 ** d)}`;
}

/** `formatJpy` ile aynı — yeni kodda bu ad kullanılsın. */
export const formatPara = formatJpy;

/**
 * Cümle içindeki tutar (repricer gerekçeleri, fiyat notları, uyarılar) — değer EN KÜÇÜK
 * birimde. JP eski biçimiyle BİREBİR: 1299 → "1299 ¥" (`binlik`: "12.140 ¥"). Diğer
 * pazarlarda `formatPara`: UK 1299 → "£12,99", MX → "MX$12,99".
 *
 * 14 Ekim 2026: bu metinler `${n} ¥` sabitti; UK/MX panellerinde peni/centavo tutarı
 * "1299 ¥" diye görünüyordu (doğrusu £12,99).
 */
export function tutarMetni(value: number, binlik = false): string {
  if (paraAyari.ondalik === 0 && paraAyari.isaret === '¥') {
    return binlik ? `${value.toLocaleString(TR)} ¥` : `${value} ¥`;
  }
  return formatJpy(value);
}

/**
 * İşaretsiz pazar tutarı (en küçük birimden): JP 12140 → "12.140" (`formatNumber` ile
 * aynı), UK 1299 → "12,99". İşareti sona koyan ekranlar için ("12.140 ¥").
 */
export function formatParaSayisi(value: number): string {
  if (paraAyari.ondalik === 0) return formatNumber(value);
  return formatNumber(value / 10 ** paraAyari.ondalik, paraAyari.ondalik);
}

/**
 * Kullanıcının yazdığı tutar (büyük birim, "12,99" / "12.99" / "12140") → en küçük
 * birim tam sayı. JP: "12140" → 12140 · UK: "12,99" → 1299. Geçersizse `null`.
 */
export function paraGirdisiniOku(metin: string): number | null {
  const temiz = metin.trim().replace(/\s/g, '');
  if (temiz === '') return null;
  // Tek ayırıcı ondalık sayılıyor; ikisi varsa sondaki ondalık
  const v = temiz.lastIndexOf(',');
  const n = temiz.lastIndexOf('.');
  const ondalikAyrac = Math.max(v, n);
  let sayi: string;
  if (v >= 0 && n >= 0) sayi = temiz.replace(ondalikAyrac === v ? /\./g : /,/g, '').replace(',', '.');
  else if (paraAyari.ondalik === 0) sayi = temiz.replace(/[.,]/g, '');
  else sayi = temiz.replace(',', '.');
  const deger = Number(sayi);
  if (!Number.isFinite(deger)) return null;
  return Math.round(deger * 10 ** paraAyari.ondalik);
}

/**
 * Girilen tutar para biriminin ondalığından fazla basamak taşıyor mu? `paraGirdisiniOku` bunu
 * yuvarlıyor; Türkçe binlik yazımı böyle 1000 kat küçülüyor: SAR'da "1.299" → 130 (SAR 1,30).
 * JP'de tek ayırıcı zaten binlik sayıldığı için bu soru yalnız iki ayırıcılı yazımda doğar.
 */
export function paraGirdisiCokOndalikli(metin: string): boolean {
  const temiz = metin.trim().replace(/\s/g, '');
  const v = temiz.lastIndexOf(',');
  const n = temiz.lastIndexOf('.');
  const ayrac = Math.max(v, n);
  if (ayrac < 0) return false;
  if (paraAyari.ondalik === 0 && (v < 0 || n < 0)) return false;
  return temiz.length - ayrac - 1 > paraAyari.ondalik;
}

/** En küçük birim → giriş kutusunda gösterilecek metin. JP 12140 → "12140" · UK 1299 → "12.99". */
export function paraGirdisineYaz(enKucukBirim: number | null | undefined): string {
  if (enKucukBirim === null || enKucukBirim === undefined) return '';
  if (paraAyari.ondalik === 0) return String(Math.round(enKucukBirim));
  return (enKucukBirim / 10 ** paraAyari.ondalik).toFixed(paraAyari.ondalik);
}

/**
 * Kullanıcının yazdığı eksi olmayan sayı: "12,50" · "12.50" · "1.234,56" · "1,234.56" · "$12,50".
 * Boş → 0. Geçersizse `null`: harf, eksi, bozuk gruplama, `ondalik`tan çok basamak.
 *
 * ⚠️ Tek ayırıcının ardında `ondalik`tan çok basamak varsa tahmin edilmiyor: tutar kutusunda
 * "1.234" hem Türkçe binlik (1234) hem ondalık (1,234) olabilir, yanlış tahmin 1000 kat fark eder.
 */
export function ondalikGirdisiniOku(metin: string, ondalik: number): number | null {
  const temiz = metin.replace(/[\s$%]/g, '');
  if (temiz === '') return 0;

  let sayi: string;
  if (/^\d+$/.test(temiz)) sayi = temiz;
  // İki farklı ayırıcı: sondaki ondalık, öndeki binlik
  else if (/^\d{1,3}(\.\d{3})+,\d*$/.test(temiz)) sayi = temiz.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(,\d{3})+\.\d*$/.test(temiz)) sayi = temiz.replace(/,/g, '');
  // Aynı ayırıcı birden çok kez: ancak binlik olabilir
  else if (/^\d{1,3}(\.\d{3}){2,}$/.test(temiz) || /^\d{1,3}(,\d{3}){2,}$/.test(temiz)) {
    sayi = temiz.replace(/[.,]/g, '');
  }
  // Tek ayırıcı: ondalık
  else if (temiz.length > 1 && /^\d*[.,]\d*$/.test(temiz)) sayi = temiz.replace(',', '.');
  else return null;

  const nokta = sayi.indexOf('.');
  if (nokta >= 0 && sayi.length - nokta - 1 > ondalik) return null;
  const deger = Number(sayi);
  return Number.isFinite(deger) ? deger : null;
}

/** 53.12 → "$53,12" */
export function formatUsd(value: number): string {
  return `$${usdFmt.format(value)}`;
}

/** Türkçede yüzde işareti ÖNDE yazılır: 20.0341 → "%20,03" */
export function formatPercent(value: number, fractionDigits = 2): string {
  return `%${formatNumber(value, fractionDigits)}`;
}

/** 0.200341 (oran) → "%20,03" */
export function formatRatioAsPercent(ratio: number, fractionDigits = 2): string {
  return formatPercent(ratio * 100, fractionDigits);
}

const dateTimeFmt = new Intl.DateTimeFormat(TR, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const dateFmt = new Intl.DateTimeFormat(TR, { day: 'numeric', month: 'short', year: 'numeric' });

/** → "23 Ağu 2026 14:32" */
export function formatDateTime(value: Date | string | number): string {
  return dateTimeFmt.format(new Date(value)).replace(',', '');
}

/** → "23 Ağu 2026" */
export function formatDate(value: Date | string | number): string {
  return dateFmt.format(new Date(value));
}

const RELATIVE_UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
  ['second', 1000],
];

const relativeFmt = new Intl.RelativeTimeFormat(TR, { numeric: 'auto' });

/**
 * → "2 saat önce". Envanter tablosundaki "Son Kontrol" kolonu bunu kullanır (§15.3.2).
 * `now` parametre olarak alınır ki fonksiyon saf kalsın ve test edilebilsin.
 */
export function formatRelativeTime(value: Date | string | number, now: Date = new Date()): string {
  const diffMs = new Date(value).getTime() - now.getTime();
  const absMs = Math.abs(diffMs);
  for (const [unit, msInUnit] of RELATIVE_UNITS) {
    if (absMs >= msInUnit || unit === 'second') {
      return relativeFmt.format(Math.round(diffMs / msInUnit), unit);
    }
  }
  return relativeFmt.format(0, 'second');
}
